<template>
    <div class="youtube-embed-widget" v-cloak>
        <form v-if="IsEditor" class="admin-control widget-admin mb-2">
            <!-- BUTTONS -->
            <button class="btn btn-sm btn-outline-dark mx-1" type="button" @click.prevent="ShowConfig = !ShowConfig">
                <i class="fa-regular fa-gear"></i>
            </button>
            <button v-if="dirty && valid" class="btn btn-sm btn-success mx-1" @click.prevent="saveChanges">Save</button>
            <button v-if="dirty" class="btn btn-sm btn-danger mx-1" @click.prevent="revertChanges">Revert</button>

            <!-- WIDGET CONFIG -->
            <div v-show="ShowConfig" class="widget-config card">
                <div class="card-body">
                    <div :class="['form-group', { 'has-error': !!YoutubeIdError } ]">
                        <label>YouTube ID <i class="fa fa-info-circle" v-tooltip="'If you know the YouTube ID, enter it, otherwise paste in a link and it will try to be obtained automatically.'"></i></label>
                        <div class="input-group">
                            <input v-model="YoutubeLink" type="text" class="form-control" placeholder="YouTube ID or link" />
                            <button :class="['btn', 'btn-success', { disabled: YoutubeLink === Config.YoutubeId }]" :disabled="YoutubeLink === Config.YoutubeId" @click.prevent="determineYoutubeID"><i class="fa fa-check"></i></button>
                        </div>
                        <span class="help-block" v-if="YoutubeIdError">{{ YoutubeIdError }}</span>
                    </div>
                </div>
            </div>
        </form>
        <div class="ratio ratio-16x9" v-if="Config.YoutubeId">
            <iframe :src="youtubeEmbedUrl" allowfullscreen></iframe>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, inject } from 'vue';

    const IsEditor = inject('IsEditor');
    const Widget = inject('Widget');
    const UpdateWidgetUrl = inject('UpdateWidgetUrl');

    const Config = ref(null);
    const OriginalConfig = ref(null);
    const ShowConfig = ref(false);
    const YoutubeLink = ref(null);
    const YoutubeIdError = ref(null);

    Config.value = _.cloneDeep(Widget.Config);
    OriginalConfig.value = _.cloneDeep(Widget.Config);
    YoutubeLink.value = Config.value.YoutubeId;

    const youtubeEmbedUrl = computed(() => "https://www.youtube.com/embed/" + Config.value.YoutubeId);

    const dirty = computed(() => {
        // check if config has changed
        let configChanged = false;
        $.each(Config.value,
            function (key, value) {
                if (key !== "Properties" && JSON.stringify(OriginalConfig.value[key]) !== JSON.stringify(value)) {
                    configChanged = true;
                    return false;
                }
            });
        return configChanged || Config.value.YoutubeId !== YoutubeLink.value;
    });

    const valid = computed(() => !YoutubeIdError.value && Config.value.YoutubeId === YoutubeLink.value);


    function saveChanges() {
        Messenger().run({
            action: $.ajax,
            successMessage: "Changes have been saved",
            errorMessage: "There was an error saving.",
            progressMessage: "Saving...",
            showCloseButton: true
        },
        {
            method: "PUT",
            url: UpdateWidgetUrl,
            data: {
                WidgetId: Widget.WidgetId,
                PageId: Widget.PageId,
                SectionId: Widget.SectionId,
                Config: Config.value,
                WidgetTypeId: Widget.WidgetTypeId
            },
            success: function (data) {
                OriginalConfig.value = _.cloneDeep(Config.value);
            }
        });
    }

    function revertChanges() {
        Config.value = _.cloneDeep(OriginalConfig.value);
        YoutubeLink.value = Config.value.YoutubeId;
    }

    function determineYoutubeID() {
        if (!YoutubeLink.value) {
            Config.value.YoutubeId = null;
        } else {
            // taken from https://stackoverflow.com/a/27728417
            let regex = /^(\w{11}$)|.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/)|(?:(?:watch)?\?v(?:i)?=|\&v(?:i)?=))([^#\&\?]*).*/;
            let match = YoutubeLink.value.match(regex);
            if (match && match.length > 1) {
                Config.value.YoutubeId = match[1] || match[2];
                YoutubeIdError.value = null;
                YoutubeLink.value = Config.value.YoutubeId;
            } else {
                YoutubeIdError.value = "Unable to determine YouTube ID";
            }
        }
    }
</script>